import './styles/page.css'
import { useEffect, useState } from "react"
import { getCookie, setCookie } from "../../config/cookies"
import { toast } from 'react-toastify'
import { reqPostNew, ApiEndpoints } from '../../config/apiClient'
import SiteSelector from './components/SiteSelector'
import RouteFilter from './components/RouteFilter'
import ClientFilter from './components/ClientFilter'
import LogsStats from './components/LogsStats'
import LogsTable from './components/LogsTable'
import RequestsByHourChart from './components/RequestsByHourChart'
import { calculateLogStats, ILogStats, extractUniqueRoutes, extractUniqueClients } from './scripts/calculateStats'
import dayjs from 'dayjs'

export interface AccessLog {
    ip: string;
    data: string;
    tz: string;
    method: string;
    rota: string;
    status: string;
    responseTime: string;
    origin: string;
    client: string;
}

function MetricasEmpresasPage() {
    const [selectedSite, setSelectedSite] = useState<string>('javali')
    const [selectedDate, setSelectedDate] = useState<string>(dayjs().format("YYYY-MM-DD"))
    const [selectedRoute, setSelectedRoute] = useState<string>('TODOS')
    const [selectedClient, setSelectedClient] = useState<string>('TODOS')
    const [logs, setLogs] = useState<AccessLog[]>([])
    const [routes, setRoutes] = useState<string[]>([])
    const [clients, setClients] = useState<string[]>([])
    const [loading, setLoading] = useState<boolean>(false)
    const [stats, setStats] = useState<ILogStats | null>(null);

    const sites = [
        { id: "javali", name: "Site Manejo Javali" },
        { id: "r2s", name: "Site R2S" },
        { id: "obportus", name: "Site OBPORTUS" },
    ];

    useEffect(() => {
        const id_usuario = getCookie("id_user");
        document.title = "Métricas Empresas | OB PORTUS";

        if (id_usuario === "") {
            let urlNow = window.location.href;
            setCookie("redirect", urlNow);
            window.location.assign(`/login?redirect=${urlNow}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function loadLogs(site: string, date: string) {
        setLoading(true)
        try {
            const payload = {
                site_nome: site,
                data_dia: date
            }

            const response = await reqPostNew(ApiEndpoints.metricasConsultarLogsSites, payload)

            if (response.status === 200) {
                const logsData = response.data || []
                
                if (Array.isArray(logsData) && logsData.length > 0) {
                    setLogs(logsData)
                    const uniqueRoutes = extractUniqueRoutes(logsData)
                    setRoutes(uniqueRoutes)
                    const uniqueClients = extractUniqueClients(logsData)
                    setClients(uniqueClients)
                    setSelectedRoute('/')
                    setSelectedClient('TODOS')
                    const calculatedStats = calculateLogStats(logsData, '/', 'TODOS')
                    setStats(calculatedStats)
                    toast.success(`${logsData.length} logs carregados com sucesso!`, {
                        containerId: 1,
                        position: 'top-center',
                        theme: 'dark'
                    })
                } else {
                    setLogs([])
                    setRoutes([])
                    setClients([])
                    setSelectedRoute('TODOS')
                    setSelectedClient('TODOS')
                    setStats(null)
                    toast.info('Nenhum log encontrado para esta data e site.', {
                        containerId: 1,
                        position: 'top-center',
                        theme: 'dark'
                    })
                }
            } else {
                setLogs([])
                setRoutes([])
                setClients([])
                setSelectedRoute('TODOS')
                setSelectedClient('TODOS')
                setStats(null)
                if (response.status !== 500 && response.status !== 400 && response.status !== 404 && response.status !== 401) { 
                    toast.error("Erro ao consultar logs", {
                        containerId: 1,
                        position: "top-center",
                        theme: "dark",
                    });
                }
            }
        } catch (error) {
            console.error('Erro ao carregar logs:', error)
            setLogs([])
            setRoutes([])
            setClients([])
            setSelectedRoute('TODOS')
            setSelectedClient('TODOS')
            setStats(null)
            toast.error('Erro ao carregar logs', {
                containerId: 1,
                position: 'top-center',
                theme: 'dark'
            })
        } finally {
            setLoading(false)
        }
    }

    async function handleSiteChange(siteId: string) {
        setSelectedSite(siteId);
    }

    async function handleDateChange(newDate: string) {
        setSelectedDate(newDate);
    }

    async function handleFilter() {
        await loadLogs(selectedSite, selectedDate)
    }

    async function handleRefresh() {
        await loadLogs(selectedSite, selectedDate)
    }

    async function handleRouteChange(route: string) {
        setSelectedRoute(route)
        const updatedStats = calculateLogStats(logs, route, selectedClient)
        setStats(updatedStats)
    }

    async function handleClientChange(client: string) {
        setSelectedClient(client)
        const updatedStats = calculateLogStats(logs, selectedRoute, client)
        setStats(updatedStats)
    }

    return (
        <div id="pageMetricasEmpresas">
            <link href="https://fonts.googleapis.com/css2?family=Roboto&display=swap" rel="stylesheet" />
            <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200" />

            <div id="filtro_painel">
                <SiteSelector sites={sites} selectedSite={selectedSite} selectedDate={selectedDate} onSiteChange={handleSiteChange} onDateChange={handleDateChange} onFilter={handleFilter} onRefresh={handleRefresh} loading={loading} />
            </div>

            {logs.length > 0 && (
                <div id="filters_painel">
                    <RouteFilter routes={routes} selectedRoute={selectedRoute} onRouteChange={handleRouteChange} loading={loading} />
                    <ClientFilter clients={clients} selectedClient={selectedClient} onClientChange={handleClientChange} loading={loading} />
                </div>
            )}

            {stats && (
                <div id="stats_container">
                    <LogsStats stats={stats} />
                </div>
            )}

            {stats && stats.requestsByHour && stats.requestsByHour.length > 0 && (
                <div id="chart_container">
                    <RequestsByHourChart stats={stats} />
                </div>
            )}

            {logs.length > 0 && (
                <div id="table_container">
                    <LogsTable logs={logs} />
                </div>
            )}

            {!loading && logs.length === 0 && stats === null && (
                <div className="no_data_container">
                    <p>Selecione um site e data para visualizar os logs</p>
                </div>
            )}
        </div>
    );
}

export default MetricasEmpresasPage;
